import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Device } from '../../../../../../../types/Device';
import {
    startAudioRecording,
    startVideoRecording,
    stopAudioRecording,
    stopVideoRecording,
    takePhotos,
} from './recordingApi';

interface RecordingState {
    isAudioModalVisible: boolean;
    isVideoModalVisible: boolean;
    isPhotoModalVisible: boolean;
    isStopModalVisible: boolean;
    stopType: 'audio' | 'video' | 'photo';
}

interface RecordingActions {
    startRecording: (type: 'audio' | 'video' | 'photo', devices: Device[]) => Promise<void>;
    stopRecording: (type: 'audio' | 'video' | 'photo', devicesToStop: Device[]) => Promise<void>;
    takePhoto: (devices: Device[]) => Promise<void>;
    setIsAudioModalVisible: (visible: boolean) => void;
    setIsVideoModalVisible: (visible: boolean) => void;
    setIsPhotoModalVisible: (visible: boolean) => void;
    setIsStopModalVisible: (visible: boolean) => void;
    setStopType: (type: 'audio' | 'video' | 'photo') => void;
    isDeviceRecording: (device: Device | null) => boolean | undefined;
    getDeviceRecordingType: (device: Device | null) => 'audio' | 'video' | 'photo' | null;
    canStartRecording: (type: 'audio' | 'video' | 'photo', devices: Device[]) => boolean;
    getDevicesWithRecording: (devices: Device[], type?: 'audio' | 'video' | 'photo') => Device[];
}

const useRecordingStore = create<RecordingState & RecordingActions>()(
    persist(
        (set, get) => ({
            isAudioModalVisible: false,
            isVideoModalVisible: false,
            isPhotoModalVisible: false,
            isStopModalVisible: false,
            stopType: 'audio',

            startRecording: async (type, devices) => {
                const deviceUIDs = devices.map((d) => d.UID);
                try {
                    if (type === 'video') {
                        await startVideoRecording(deviceUIDs);
                    } else if (type === 'audio') {
                        await startAudioRecording(deviceUIDs);
                    }
                } catch (error) {
                    console.error(`Error starting ${type} recording:`, error);
                    throw error;
                }
            },

            stopRecording: async (type, devicesToStop) => {
                const deviceUIDs = devicesToStop.map((d) => d.UID);
                try {
                    if (type === 'video') {
                        await stopVideoRecording(deviceUIDs);
                    } else if (type === 'audio') {
                        await stopAudioRecording(deviceUIDs);
                    }
                } catch (error) {
                    console.error(`Error stopping ${type} recording:`, error);
                    throw error;
                }
            },

            takePhoto: async (devices) => {
                const deviceUIDs = devices.map((d) => d.UID);
                try {
                    await takePhotos(deviceUIDs);
                } catch (error) {
                    console.error('Error taking photos:', error);
                    throw error;
                }
            },

            // isDeviceRecording: (deviceUID, devices) => {
            //     const device = devices.find((d) => d.UID === deviceUID);
            //     if (!device) return false;
            //     return (
            //         device.currentStatus?.includes('аудио') ||
            //         device.currentStatus?.includes('видео')
            //     );
            // },
            //
            // getDeviceRecordingType: (deviceUID, devices) => {
            //     const device = devices.find((d) => d.UID === deviceUID);
            //     if (!device) return null;
            //     if (device.currentStatus?.includes('видео')) return 'video';
            //     if (device.currentStatus?.includes('аудио')) return 'audio';
            //     return null;
            // },

            isDeviceRecording: (device) => {
                const status = device?.currentStatus || '';
                return status.includes('пишет аудио') || status.includes('пишет видео');
            },

            getDeviceRecordingType: (device) => {
                const status = device?.currentStatus || '';
                if (status.includes('пишет видео')) return 'video';
                if (status.includes('пишет аудио')) return 'audio';
                if (status.includes('остановка аудио')) return 'audio';
                if (status.includes('остановка видео')) return 'video';
                return null;
            },

            canStartRecording: (type, devices) => {
                return devices.every((device) => {
                    const currentType = get().getDeviceRecordingType(device);

                    if (!currentType) return true;
                    if (currentType === 'video' && type === 'audio') return false;
                    if (currentType === 'audio' && type === 'video') return true;
                    if (currentType === type) return true;

                    return false;
                });
            },

            getDevicesWithRecording: (devices, type) => {
                return devices.filter((device) => {
                    const status = device.currentStatus || '';

                    if (status.includes('остановка') || status == '') return false;

                    if (!type)
                        return status.includes('пишет аудио') || status.includes('пишет видео');
                    if (type === 'audio') return status.includes('пишет аудио');
                    if (type === 'video') return status.includes('пишет видео');
                    return false;
                });
            },

            setIsAudioModalVisible: (visible) => set({ isAudioModalVisible: visible }),
            setIsVideoModalVisible: (visible) => set({ isVideoModalVisible: visible }),
            setIsPhotoModalVisible: (visible) => set({ isPhotoModalVisible: visible }),
            setIsStopModalVisible: (visible) => set({ isStopModalVisible: visible }),
            setStopType: (type) => set({ stopType: type }),
        }),
        {
            name: 'recording-storage',
        },
    ),
);

export default useRecordingStore;
